/**
 * "Task created" toast with an Open action (FRIDAY-73).
 *
 * Quick creation only asks for a title. The toast names the new task and
 * offers to open it, so the user can add the rest of the details right away.
 */

import { useCallback } from 'react'
import { useRouter } from '@tanstack/react-router'
import type { Task } from '@friday/shared/realtime'
import { toast } from '@/features/feedback/toast'

/** The board key of a task key: `FRIDAY-12` → `FRIDAY`. */
export function projectKeyOfTask(taskKey: string): string {
  const dash = taskKey.lastIndexOf('-')
  return dash > 0 ? taskKey.slice(0, dash) : taskKey
}

/**
 * Returns a function that announces a created task. Outside a router (some
 * component harnesses) the toast still shows, without the Open action.
 */
export function useTaskCreatedToast(wsId: string | undefined) {
  const router = useRouter({ warn: false })
  return useCallback(
    (task: Pick<Task, 'key' | 'title'>) => {
      const open =
        router && wsId
          ? {
              label: 'Open task',
              onSelect: () => {
                void router.navigate({
                  to: '/w/$wsId/p/$pKey/t/$tKey',
                  params: { wsId, pKey: projectKeyOfTask(task.key), tKey: task.key },
                })
              },
            }
          : undefined
      toast(`Created ${task.key} · ${task.title}`, { kind: 'success', action: open })
    },
    [router, wsId],
  )
}
